import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, Users, Timer, Calendar, Lightbulb, Clock, UserSearch, CalendarClock, X, CloudOff } from 'lucide-react';
import PropTypes from 'prop-types';
import PersonRow from './search/PersonRow';
import ProfileSheet from './profile/ProfileSheet.jsx';
import Card from './ui/Card.jsx';
import Skeleton from './ui/Skeleton.jsx';
import SessionCard from './SessionCard.js';
import Tabs from './ui/Tabs';
import PageShell from './ui/PageShell';
import EmptyState from './ui/EmptyState.jsx';
import Button from './ui/Button';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/ApiService';
import { apiService } from '../services/api'; // Keep for getSchoolSkills
import browserCacheService from '../services/BrowserCacheService';
import ChatService from '../services/ChatService';
import { SESSION_STATUS } from '../constants/SessionTypes';

// Text, wenn die Suche den Server nicht erreicht. Ein leeres Ergebnis ist KEIN Fehler.
const SEARCH_ERROR_TEXT = 'Die Suche hat gerade nicht geklappt';

// Treffer pro Seite (Backend-Vorgabe ist dieselbe).
const PAGE_SIZE = 20;

// Pagination steht im Backend unter `meta` ({ page, totalCount, hasMore }). Bis Okt 2026
// las die Suche `response.hasMore`, das es nie gab: "Weitere laden" erschien nie, mehr
// als 20 Treffer blieben unsichtbar. Aeltere Antwortformen bleiben lesbar.
export const readSearchMeta = (response) => {
  const meta = response?.meta || response?.data?.meta || {};
  const hasMore = Boolean(meta.hasMore ?? response?.hasMore ?? false);
  const rawTotal = meta.totalCount ?? response?.totalCount;
  const totalCount = Number.isFinite(rawTotal) ? rawTotal : null;
  return { hasMore, totalCount };
};

// Haengt Seite 2 ff. an, ohne dieselbe Person doppelt zu zeigen.
const appendUnique = (prev, next) => {
  const seen = new Set(prev.map(p => p.id));
  return [...prev, ...next.filter(p => !seen.has(p.id))];
};

// CRITICAL FIX: Use correct ApiService instance
const apiServiceInstance = new ApiService();

// Deduplizierungsfunktion für Skills
const deduplicateSkills = (skills) => {
  if (!skills || !Array.isArray(skills)) return [];

  const uniqueSkills = [];
  const lowerCaseMap = new Map();

  for (const skill of skills) {
    if (!skill) continue;

    const skillString = typeof skill === 'string' ? skill :
      (typeof skill === 'object' && skill.name) ? skill.name : '';

    if (!skillString.trim()) continue;

    const skillLower = skillString.toLowerCase().trim();

    if (!lowerCaseMap.has(skillLower)) {
      lowerCaseMap.set(skillLower, skillString);
      uniqueSkills.push(skillString);
    }
  }


  return uniqueSkills;
};

export const AdvancedSearch = ({
  profiles,
  onSearchResults,
  sessions,
  currentUser,
  onConnect,
  onJoinSession,
  initialFilters = {},
  autoFocus = false,
  // Vorbelegter Suchbegriff, z. B. „Personen mit „Excel“ suchen“ aus dem Reiter
  // „Skills“ der Startseite. Wird nur beim Öffnen übernommen.
  initialSearchTerm = ''
}) => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm || '');
  const [activeTab, setActiveTab] = useState('people');
  const [searchResults, setSearchResults] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allSkills, setAllSkills] = useState([]);
  // Die Begriffe stehen in jedem Treffer selbst (highlightTerms); der Stand
  // hier wird nur noch gesetzt, weil die Suchpfade ihn mitfuehren.
  const [, setHighlightTerms] = useState([]);
  // Tipp auf eine Trefferzeile oeffnet die Profilkarte als Blatt (Okt 2026)
  const [openedProfile, setOpenedProfile] = useState(null);

  // Wer aus der Profilkarte heraus blockiert, soll die Person sofort nicht
  // mehr sehen: aus der Liste nehmen und den Suchspeicher des Geraets leeren,
  // sonst kaeme sie bei derselben Suche bis zu 2 Minuten zurueck.
  const handleBlocked = useCallback((blockedUserId) => {
    setSearchResults((prev) => prev.filter((p) => (p.userId || p.id) !== blockedUserId && p.id !== blockedUserId));
    browserCacheService.invalidateSearch();
    setOpenedProfile(null);
  }, []);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  // "Weitere laden" hat einen eigenen Zustand: Seite 2 soll die sichtbaren
  // Treffer weder durch Platzhalter ersetzen noch bei einem Fehler loeschen.
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  // Gesamtzahl laut Server (meta.totalCount); null = unbekannt
  const [totalCount, setTotalCount] = useState(null);
  const currentPageRef = useRef(1);
  currentPageRef.current = currentPage;
  const [isMobile, setIsMobile] = useState(false);
  const searchInputRef = useRef(null);
  const searchBoxRef = useRef(null);

  // Der Begriff, der gerade im Feld steht. Jede Antwort wird daran gemessen:
  // Kommt sie erst, wenn schon etwas anderes getippt wurde (30-s-Hintergrund-
  // Refresh, langsame Antwort, "Weitere laden"), gehört sie nicht mehr zur
  // Anzeige. Bis September 2026 ersetzte der Refresh eines alten Cache-Treffers
  // die Englisch-Treffer 30 s später durch Mathe-Profile samt Mathe-Hervorhebung.
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;
  const backgroundRefreshRef = useRef(null);

  const cancelBackgroundRefresh = () => {
    if (backgroundRefreshRef.current) {
      clearTimeout(backgroundRefreshRef.current);
      backgroundRefreshRef.current = null;
    }
  };

  // Nach dem Verlassen der Suche keine Anfrage mehr absetzen.
  useEffect(() => () => {
    if (backgroundRefreshRef.current) clearTimeout(backgroundRefreshRef.current);
  }, []);

  // Vorschlagsliste schliessen, wenn daneben getippt/geklickt wird.
  // Ohne das blieb sie auf dem Handy offen und verdeckte die Treffer.
  useEffect(() => {
    if (!showSuggestions) return undefined;

    const handleOutside = (event) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [showSuggestions]);

  // Autofokus nach der Slide-in-Animation (iOS nimmt den Fokus erst
  // zuverlässig an, wenn die Transition abgeschlossen ist)
  useEffect(() => {
    if (!autoFocus) return undefined;
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 250);
    return () => clearTimeout(timer);
  }, [autoFocus]);

  // Verbindungsstatus der Treffer nachladen (September 2026, Beta-Test).
  // Die Suche lieferte Profile ohne `connectionStatus` — die Karte zeigte dann
  // auch bei bestehender Vernetzung "Vernetzen" statt "Zum Chat". Ein
  // Batch-Aufruf wie in der Entdecken-Ansicht (useProfileLoader) reicht.
  const statusInFlightRef = useRef(new Set());
  useEffect(() => {
    const missing = [...new Set(searchResults
      .filter((p) => p.userId && !p.connectionStatus)
      .map((p) => p.userId))]
      .filter((id) => !statusInFlightRef.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => statusInFlightRef.current.add(id));
    ChatService.getConnectionStatusBatch(missing).then((statuses) => {
      missing.forEach((id) => statusInFlightRef.current.delete(id));
      // Ohne Antwort (Netzfehler) bleibt die Karte bei "Vernetzen"; das Backend
      // lehnt eine doppelte Anfrage ohnehin ab (ALREADY_CONNECTED).
      if (!statuses || Object.keys(statuses).length === 0) return;
      setSearchResults((prev) => prev.map((p) => (
        missing.includes(p.userId) && !p.connectionStatus
          ? { ...p, connectionStatus: statuses[p.userId] || { status: 'none' } }
          : p
      )));
    });
  }, [searchResults]);

  // Prüfe ob Mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Lade Skills für intelligente Vorschläge
  useEffect(() => {
    const loadSchoolSkills = async () => {
      const schoolId = currentUser?.schoolId || user?.schoolId;
      if (!schoolId) {
        return;
      }

      try {
        const skillsData = await apiService.getSchoolSkills(schoolId);

        // Flatten alle Skills in eine einzige Liste für Suggestions
        // Nur echte Namen: Ein unerwartetes Antwortformat liess hier undefined durch,
        // und die Vorschlagssuche stuerzte dann bei toLowerCase() ab.
        const flatSkills = Object.values(skillsData || {}).flat()
          .map(skill => skill?.name)
          .filter(name => typeof name === 'string' && name);
        setAllSkills(flatSkills);
      } catch (error) {
        console.error('Fehler beim Laden der Skills für Suche:', error);
        // Fallback zu Demo-Skills
        const demoSkills = [
          'Mathematik', 'Deutsch', 'Englisch', 'Physik', 'Chemie', 'Biologie',
          'Programmieren', 'Webdesign', 'JavaScript', 'Python',
          'Zeichnen', 'Fotografie', 'Musik', 'Gitarre', 'Klavier',
          'Geschichte', 'Geographie', 'Sport', 'Fußball', 'Basketball'
        ];
        setAllSkills(demoSkills);
      }
    };

    loadSchoolSkills();
  }, [currentUser?.schoolId, user?.schoolId]);

  // Intelligente Suchvorschläge generieren
  useEffect(() => {
    if (searchTerm.length >= 2) {
      const term = searchTerm.toLowerCase();

      // Sammle alle verfügbaren Suchbegriffe
      const suggestions = new Set();

      // Skills als Vorschläge
      allSkills.forEach(skill => {
        if (skill.toLowerCase().includes(term)) {
          suggestions.add(skill);
        }
      });

      // Namen als Vorschläge (nur ersten Teil für Privatsphäre)
      profiles.forEach(profile => {
        if (profile.name && profile.name.toLowerCase().includes(term)) {
          const firstName = profile.name.split(' ')[0];
          suggestions.add(firstName);
        }
      });

      // Kategorien als Vorschläge
      const categories = ['Schulfächer', 'Technik', 'Kreativ', 'Sport', 'Musik', 'Sprachen'];
      categories.forEach(category => {
        if (category.toLowerCase().includes(term)) {
          suggestions.add(category);
        }
      });

      // Was schon im Feld steht, ist kein Vorschlag mehr — sonst verdeckt die
      // Liste mit genau diesem Wort die Reiter darunter.
      const fresh = Array.from(suggestions).filter(s => s.toLowerCase() !== term.trim());
      setSearchSuggestions(fresh.slice(0, 8));
      setShowSuggestions(fresh.length > 0);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchTerm, allSkills, profiles]);

  // IMMEDIATE FIX: Smart Local Search mit Relevanz-Scoring
  const performLocalSearch = (term) => {
    if (!term.trim() || !profiles.length) return [];

    const searchTerm = term.toLowerCase().trim();


    const scoredProfiles = profiles
      .filter(profile => profile.id !== currentUser?.id)
      .map(profile => {
        let score = 0;
        const reasons = [];

        // 1. Name-Match (höchste Priorität)
        const name = (profile.displayName || profile.name || '').toLowerCase();
        if (name.includes(searchTerm)) {
          score += 100;
          reasons.push('Name-Match');
        }

        // 2. Skills-Match (hohe Priorität)
        const offeredSkills = profile.tenant_skills_OfferedSkills || profile.offeredSkills || [];
        const soughtSkills = profile.tenant_skills_SoughtSkills || profile.soughtSkills || [];

        [...offeredSkills, ...soughtSkills].forEach(skill => {
          const skillName = (typeof skill === 'string' ? skill : skill.name || '').toLowerCase();
          if (skillName.includes(searchTerm)) {
            score += 80;
            reasons.push(`Skill: ${skillName}`);
          }
        });

        // 3. Bio-Match (mittlere Priorität)
        const bio = (profile.bio || '').toLowerCase();
        if (bio.includes(searchTerm)) {
          score += 40;
          reasons.push('Bio-Match');
        }

        // 4. Interests-Match (niedrige Priorität)
        const interests = profile.interests || [];
        interests.forEach(interest => {
          const interestLower = interest.toLowerCase();
          if (interestLower.includes(searchTerm)) {
            score += 20;
            reasons.push(`Interest: ${interest}`);
          }
        });

        return {
          ...profile,
          searchScore: score,
          searchReasons: reasons,
          highlightTerms: [term]
        };
      })
      .filter(profile => profile.searchScore > 0)
      .sort((a, b) => b.searchScore - a.searchScore);



    return scoredProfiles;
  };

  // STUFE 4: Enhanced Smart Search mit Browser-Cache und UX-Features
  const performSmartSearch = async (term, page = 1, isBackgroundRefresh = false) => {
    // Jede neue Suche ersetzt einen noch ausstehenden Hintergrund-Refresh.
    if (!isBackgroundRefresh) cancelBackgroundRefresh();
    const isStale = () => term !== searchTermRef.current;

    if (!term.trim()) {
      // Keine Suche - zeige lokale Profile als Fallback
      const fallbackProfiles = profiles.filter(profile =>
        profile.id !== currentUser?.id
      ).slice(0, 20);
      setSearchResults(fallbackProfiles);
      setHighlightTerms([]);
      setHasMore(false);
      setTotalCount(null);
      setCurrentPage(1);
      setLoadMoreFailed(false);
      // Eine verworfene Antwort (siehe isStale) setzt das Laden nicht mehr
      // zurück — nach dem Leeren des Feldes muss das hier geschehen.
      setIsSearchLoading(false);
      return;
    }

    const isLoadMore = page > 1 && !isBackgroundRefresh;
    if (isLoadMore) {
      setIsLoadingMore(true);
      setLoadMoreFailed(false);
    } else if (!isBackgroundRefresh) {
      setIsSearchLoading(true);
      setSearchError(null);
      setLoadMoreFailed(false);
    }

    try {


      // STUFE 4: Browser Cache Check für Suchresultate
      const cachedSearchResult = browserCacheService.getSearchResults(term);
      if (cachedSearchResult && !isBackgroundRefresh && page === 1) {


        const processedProfiles = cachedSearchResult.results.map(profile => ({
          ...profile,
          highlightTerms: [term],
          // 🔧 FIX: Defensive Fallback für availability (Support für alte Cache-Daten)
          availability: profile.availability || profile.availability_slots || {}
        }));

        setSearchResults(processedProfiles);
        setHighlightTerms([term]);
        // Der Cache haelt nur Seite 1, merkt sich aber, ob es weitergeht.
        setHasMore(Boolean(cachedSearchResult.hasMore));
        setTotalCount(Number.isFinite(cachedSearchResult.totalCount) ? cachedSearchResult.totalCount : null);
        setCurrentPage(1);
        setIsSearchLoading(false);

        // Background refresh nach 30 sekunden für frische Daten — nur, wenn der
        // Begriff dann noch im Feld steht und niemand weitere Seiten geladen hat
        // (der Refresh ersetzt die Liste durch Seite 1).
        backgroundRefreshRef.current = setTimeout(() => {
          backgroundRefreshRef.current = null;
          if (isStale() || currentPageRef.current > 1) return;
          performSmartSearch(term, 1, true);
        }, 30000);

        return;
      }

      const searchData = {
        searchTerm: term,
        filters: {
          // Optionale Filter können hier hinzugefügt werden
          role: ['STUDENT', 'TEACHER'],
          minScore: 0
        },
        page,
        limit: PAGE_SIZE
      };



      // CRITICAL FIX: Use correct ApiService instance
      const response = await apiServiceInstance.searchProfiles(
        searchData.searchTerm,
        searchData.filters,
        searchData.page,
        searchData.limit
      );

      // Inzwischen steht ein anderer Begriff im Feld: Die Antwort ist veraltet.
      if (isStale()) return;

      // Serverfehler sind kein leeres Ergebnis. Der ApiService wirft bei success:false
      // schon selbst; die Pruefung hier faengt andere Aufrufwege ab.
      if (!response || response.success === false) {
        throw new Error('Suche nicht verfügbar');
      }
      const { hasMore: nextHasMore, totalCount: nextTotal } = readSearchMeta(response);


      // Handle different response structures
      let backendProfiles = [];
      if (response.profiles) {
        backendProfiles = response.profiles;
      } else if (response.data && response.data.profiles) {
        backendProfiles = response.data.profiles;
      } else if (response.success && response.data && response.data.profiles) {
        backendProfiles = response.data.profiles;
      }



      if (backendProfiles && backendProfiles.length > 0) {
        // Verarbeite Profile und füge Highlight-Informationen hinzu
        const processedProfiles = backendProfiles.map(profile => ({
          ...profile,
          highlightTerms: [term], // Einfaches Highlighting des Suchbegriffs
          // 🔧 FIX: Defensive Fallback für availability (Support für alte API Responses)
          availability: profile.availability || profile.availability_slots || {}
        }));

        if (page === 1) {
          setSearchResults(processedProfiles);

          // STUFE 4: Cache neue Suchresultate im Browser (nur echte Treffer;
          // Fehler landen nie hier, sie werfen oben bzw. im ApiService)
          if (!isBackgroundRefresh) {
            browserCacheService.setSearchResults(term, processedProfiles, {
              hasMore: nextHasMore,
              totalCount: nextTotal,
            });
          }
        } else {
          setSearchResults(prev => appendUnique(prev, processedProfiles));
        }

        setHasMore(nextHasMore);
        setTotalCount(nextTotal);
        setHighlightTerms([term]);
        setCurrentPage(page);
      } else if (isLoadMore) {
        // Leere Folgeseite: Es gibt nichts mehr, der Knopf verschwindet.
        setHasMore(false);
      } else if (!isBackgroundRefresh) {
        // Kein Treffer: Ergebnisse aktiv leeren. Ohne diesen Zweig blieben die vorherigen
        // Treffer (bzw. die 20 Start-Profile) stehen - bei einer Unsinn-Eingabe sah es aus,
        // als wuerden weiterhin "alle Leute" passen.
        // Kein Fehler, sondern ein leeres Ergebnis: Das zeigt der ruhige
        // EmptyState unten. Bis Okt 2026 stand hier zusaetzlich ein roter
        // Alarmkasten mit "Erneut versuchen" (Heikos Screenshot).
        setSearchResults([]);
        setHasMore(false);
        setTotalCount(0);
        setCurrentPage(1);
        setHighlightTerms([]);
      }
    } catch (error) {
      console.error('Backend Search Error:', error);

      if (isLoadMore) {
        // Die schon geladenen Treffer bleiben stehen; der Knopf bleibt fuer einen
        // neuen Versuch, darueber ein ruhiger Satz.
        if (!isStale()) setLoadMoreFailed(true);
      } else if (!isBackgroundRefresh && !isStale()) {
        // ARCHITECT MODE: Intelligente lokale Suche als Fallback
        try {
          const localResults = performLocalSearch(term);
          if (localResults.length > 0) {

            setSearchResults(localResults);
            setHighlightTerms([term]);
            setHasMore(false);
            setTotalCount(null);
            setCurrentPage(1);
            setSearchError(null); // Clear error da Fallback funktioniert
          } else {
            // Der Server war nicht erreichbar: Das ist ein Fehler, kein leeres Ergebnis.
            setSearchError(SEARCH_ERROR_TEXT);
            setSearchResults([]);
            setHasMore(false);
            setHighlightTerms([]);
          }
        } catch (localError) {
          console.error('Local Search Fallback failed:', localError);
          // Keine Technik im Text (CorporateDesign, Feedback in der App).
          setSearchError(SEARCH_ERROR_TEXT);
          setSearchResults([]);
          setHasMore(false);
          setHighlightTerms([]);
        }
      }
    } finally {
      // Eine veraltete Antwort beendet nicht das Laden der aktuellen Suche.
      if (isLoadMore) {
        setIsLoadingMore(false);
      } else if (!isBackgroundRefresh && !isStale()) {
        setIsSearchLoading(false);
      }
    }
  };

  // Smart Search mit Debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSmartSearch(searchTerm, 1);
    }, 500); // 500ms Debounce für bessere Performance

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Initialer Load für leere Suche
  useEffect(() => {
    if (!searchTerm) {
      const initialProfiles = profiles.filter(profile =>
        profile.id !== currentUser?.id
      ).slice(0, 20);
      setSearchResults(initialProfiles);
      setHighlightTerms([]);
      setHasMore(false);
    }
  }, [profiles, currentUser]);

  // Separate useEffect für onSearchResults, um Infinite Loop zu vermeiden
  useEffect(() => {
    // Überprüfe, ob sich die Ergebnisse tatsächlich geändert haben,
    // bevor onSearchResults aufgerufen wird
    const resultsJSON = JSON.stringify(searchResults.map(r => r.id));

    // Vergleiche mit dem vorherigen Zustand
    if (resultsJSON !== searchResultsRef.current) {
      searchResultsRef.current = resultsJSON;
      onSearchResults(searchResults);
    }
  }, [searchResults, onSearchResults]);

  // Referenz für Suchresultate, um unnötige Updates zu vermeiden
  const searchResultsRef = React.useRef('[]');

  // Session-Ableitungen memoisiert: dieser Block lief bisher bei JEDEM Render neu
  // (jeder Tastenanschlag + jede der ~8-10 State-Aenderungen pro Suche) komplett durch
  // die Sessions-Liste, inkl. toLowerCase() pro Session. Jetzt nur noch bei Aenderung
  // von sessions/searchTerm. Der Suchbegriff wird einmal statt pro Session lowergecased.
  const { activeSessions, plannedSessions } = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const filtered = (sessions || []).filter(session =>
      session.topic.toLowerCase().includes(term) ||
      session.location.toLowerCase().includes(term)
    );
    return {
      activeSessions: filtered.filter(s => s.status === SESSION_STATUS.ACTIVE),
      plannedSessions: filtered.filter(s => s.status === SESSION_STATUS.PLANNED),
    };
  }, [sessions, searchTerm]);

  // Zaehlzeile: die echte Gesamtzahl, sobald der Server sie nennt (mehr als eine Seite).
  const peopleCount = Number.isFinite(totalCount)
    ? Math.max(totalCount, searchResults.length)
    : searchResults.length;

  const countLabel = activeTab === 'people'
    ? (searchError ? null : `${peopleCount} ${peopleCount === 1 ? 'Person' : 'Personen'} gefunden`)
    : activeTab === 'active'
      ? `${activeSessions.length} aktive ${activeSessions.length === 1 ? 'Session' : 'Sessions'}`
      : `${plannedSessions.length} geplante ${plannedSessions.length === 1 ? 'Session' : 'Sessions'}`;

  // Handler für Suchvorschläge
  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    setShowSuggestions(false);
  };

  const handleSearchClear = () => {
    setSearchTerm('');
    setShowSuggestions(false);
    setHighlightTerms([]);
  };

  // Stabiler Handler fuer SessionCard (React.memo): bisher wurde onValidateCode als
  // Inline-Arrow bei jedem Render neu erzeugt und hat damit die Memoisierung der
  // SessionCards ausgehebelt - alle sichtbaren Karten renderten bei jedem Tastenanschlag
  // neu. Als useCallback bleibt die Referenz stabil (nur onJoinSession als Dep).
  const handleValidateCode = useCallback((sessionId, code) => {
    // Promise zurueckgeben: SessionCard awaited den Handler und zeigt seine
    // Bestaetigung nur bei Erfolg - ohne return resolved das await sofort.
    return onJoinSession(sessionId, code);
  }, [onJoinSession]);

  return (
    // Seitenkopf wie bei den anderen Zielen der Bottom-Navigation (Bungee-Titel).
    <PageShell variant="main" embedded title="Suche">
    <div className="w-full">
      {/* Fixed Magical Search Bar - Mobile Optimized */}
      <div
        className="sticky z-30"
        style={{ top: 0 }}
      >
        <div className="px-0">
          <div className="w-full space-y-3 rounded-2xl border border-border bg-surface p-3 shadow-token-sm md:p-4">
            {/* Magical Search Bar */}
            <div className="relative" ref={searchBoxRef}>
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={isMobile ? 'Personen, Skills, Interessen …' : 'Suche nach Personen, Skills, Interessen …'}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowSuggestions(e.target.value.length >= 2);
                  }}
                  onFocus={() => setShowSuggestions(searchTerm.length >= 2)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.target.blur(); // Keyboard auf Mobile schließen
                      setShowSuggestions(false);
                    }
                  }}
                  className="h-12 w-full rounded-xl border border-border bg-surface pl-11 pr-12 text-base text-foreground transition-colors duration-base placeholder:text-faint focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/15"
                />
                <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-faint" size={18} aria-hidden="true" />

                {/* Clear Button */}
                {searchTerm && (
                  <button
                    type="button"
                    onClick={handleSearchClear}
                    aria-label="Suche zurücksetzen"
                    className="absolute right-1.5 md:right-2 top-1/2 transform -translate-y-1/2 h-10 w-10 inline-flex items-center justify-center rounded-full text-faint hover:text-foreground hover:bg-foreground/5 transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              </div>

              {/* Intelligente Suchvorschläge */}
              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-surface-overlay rounded-xl shadow-token-lg border border-border z-dropdown max-h-64 overflow-y-auto">
                  <div className="p-2">
                    <div className="text-xs text-muted-foreground font-medium mb-2 px-2 flex items-center gap-1.5">
                      <Lightbulb className="h-3.5 w-3.5" />
                      Vorschläge für dich:
                    </div>
                    {searchSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full text-left px-3 py-2 rounded-lg hover:bg-primary-soft hover:text-primary transition-colors duration-fast text-sm flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                      >
                        <span>{suggestion}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Kanonische Tab-Leiste; die Zaehler laufen jetzt ueber `count`
                statt als Klammerzusatz im Label. */}
            <Tabs
              variant="pill"
              ariaLabel="Suchbereiche"
              value={activeTab}
              onChange={setActiveTab}
              items={[
                { key: 'people', label: 'Personen', icon: Users },
                {
                  key: 'active',
                  label: 'Aktive Sessions',
                  shortLabel: 'Aktiv',
                  icon: Timer,
                  count: activeSessions.length,
                },
                {
                  key: 'planned',
                  label: 'Geplante Sessions',
                  shortLabel: 'Geplant',
                  icon: Calendar,
                  count: plannedSessions.length,
                },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Search Results Area */}
      <div className="pt-5 md:pt-6 pb-20 animate-fade-in">
        <div className="w-full px-2 sm:px-3">
          {/* Zaehlzeile wie im Spot statt Infobox mit Funkeln (Okt 2026) */}
          {searchTerm && (
            <div className="mb-2 flex items-center justify-between gap-3 px-1">
              {/* Zwei Einheiten, die nie mitten im Wort brechen: Die Zahl samt "·"
                  bleibt zusammen (der Punkt steht nie allein am Zeilenanfang), der
                  Suchbegriff rutscht auf schmalen Telefonen in die zweite Zeile und
                  wird erst dort gekuerzt. Bei einem Fehler waere "0 Personen
                  gefunden" gelogen: dann nur der Begriff. */}
              <p className="flex min-w-0 flex-wrap items-baseline gap-x-1 text-xs text-faint">
                {countLabel && <span className="whitespace-nowrap">{countLabel} ·</span>}
                {countLabel && ' '}
                <span className="min-w-0 max-w-full truncate">
                  {'Suchbegriff „'}{searchTerm.trim()}{'“'}
                </span>
              </p>
              <button
                type="button"
                onClick={handleSearchClear}
                className="shrink-0 rounded-lg px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                Suche löschen
              </button>
            </div>
          )}

          {activeTab === 'people' ? (
            <div className="space-y-4">
              {/* Fehler nur, wenn die Suche wirklich nicht durchkam. Ruhig wie ein
                  leerer Zustand: graues Icon, ein Satz, ein Knopf - kein Alarmkasten. */}
              {searchError && !isSearchLoading && (
                <div role="status">
                  <EmptyState
                    size="sm"
                    icon={CloudOff}
                    title={searchError}
                    text="Prüf kurz deine Verbindung und versuch es dann noch einmal."
                    action={(
                      <Button variant="secondary" onClick={() => performSmartSearch(searchTerm, 1)}>
                        Noch mal suchen
                      </Button>
                    )}
                  />
                </div>
              )}

              {/* Treffer als ruhige Zeilen wie im Spot (Okt 2026); ein Tipp oeffnet
                  die Profilkarte als Blatt. */}
              {isSearchLoading ? (
                <Card className="divide-y divide-border px-2 py-1" aria-busy="true">
                  <span className="sr-only">Suche läuft…</span>
                  {Array.from({ length: 5 }).map((_, index) => <Skeleton.Row key={`skeleton-${index}`} />)}
                </Card>
              ) : searchResults.length > 0 && (
                <Card className="mx-auto max-w-2xl divide-y divide-border px-2 py-1">
                  {searchResults.map(profile => (
                    <PersonRow
                      key={profile.id}
                      profile={profile}
                      searchTerm={searchTerm}
                      onOpen={setOpenedProfile}
                    />
                  ))}
                </Card>
              )}

              {/* STUFE 4: Loading State entfernt - ersetzt durch Skeleton Loading */}

              {/* Load More Button */}
              {hasMore && !isSearchLoading && !searchError && searchResults.length > 0 && (
                <div className="flex flex-col items-center gap-2 py-6">
                  {loadMoreFailed && (
                    <p role="status" className="text-sm text-muted-foreground">
                      Weitere Ergebnisse kamen gerade nicht an.
                    </p>
                  )}
                  <Button
                    variant="secondary"
                    disabled={isLoadingMore}
                    aria-busy={isLoadingMore || undefined}
                    onClick={() => performSmartSearch(searchTerm, currentPage + 1)}
                  >
                    {isLoadingMore ? 'Lädt …' : 'Weitere Ergebnisse laden'}
                  </Button>
                </div>
              )}

              {/* No Results */}
              {/* Kein Treffer ist kein Fehler. "Suche löschen" steht schon in der
                  Zaehlzeile darueber, deshalb hier kein zweiter Knopf. */}
              {searchResults.length === 0 && searchTerm && !isSearchLoading && !searchError && (
                <EmptyState
                  className="col-span-full"
                  size="sm"
                  icon={Search}
                  title={`Zu „${searchTerm.trim()}“ passt noch niemand`}
                  text="Probier einen Skill wie Mathe oder einen Vornamen."
                />
              )}
              {searchResults.length === 0 && !searchTerm && (
                <EmptyState
                  className="col-span-full"
                  icon={UserSearch}
                  title="Entdecke deine Mitschüler"
                  text="Nutze die Suche oben, um Personen mit bestimmten Skills oder Interessen zu finden"
                />
              )}
            </div>
          ) : activeTab === 'active' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {activeSessions.map(session => (
                <SessionCard
                  key={session.id}
                  session={session}
                  currentUser={currentUser}
                  onJoinSession={onJoinSession}
                  onValidateCode={handleValidateCode}
                />
              ))}
              {activeSessions.length === 0 && (
                <EmptyState
                  className="col-span-full"
                  icon={Clock}
                  title={searchTerm ? 'Keine aktiven Sessions gefunden' : 'Keine aktiven Sessions'}
                  text={searchTerm
                      ? 'Versuche es mit anderen Suchbegriffen'
                      : 'Erstelle eine neue Session oder schaue bei den geplanten Sessions vorbei'
                    }
                />
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {plannedSessions.map(session => (
                <SessionCard
                  key={session.id}
                  session={session}
                  currentUser={currentUser}
                  onJoinSession={onJoinSession}
                  onValidateCode={handleValidateCode}
                />
              ))}
              {plannedSessions.length === 0 && (
                <EmptyState
                  className="col-span-full"
                  icon={CalendarClock}
                  title={searchTerm ? 'Keine geplanten Sessions gefunden' : 'Keine geplanten Sessions'}
                  text={searchTerm
                      ? 'Versuche es mit anderen Suchbegriffen'
                      : 'Plane eine neue Session für später oder schaue bei den aktiven Sessions vorbei'
                    }
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    {openedProfile && (
      <ProfileSheet
        open
        onClose={() => setOpenedProfile(null)}
        profile={openedProfile}
        name={openedProfile.displayName || openedProfile.name}
        searchTerm={searchTerm}
        onBlocked={handleBlocked}
        origin="SEARCH"
      />
    )}
    </PageShell>
  );
};

AdvancedSearch.propTypes = {
  profiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      age: PropTypes.number,
      gender: PropTypes.string,
      distance: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      offers: PropTypes.arrayOf(PropTypes.string)
    })
  ).isRequired,
  onSearchResults: PropTypes.func.isRequired,
  sessions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      topic: PropTypes.string.isRequired,
      location: PropTypes.string.isRequired,
      status: PropTypes.oneOf(['PLANNED', 'WAITING', 'ACTIVE', 'COMPLETED', 'CANCELLED']).isRequired
    })
  ),
  currentUser: PropTypes.object.isRequired,
  onConnect: PropTypes.func.isRequired,
  onJoinSession: PropTypes.func.isRequired,
  initialFilters: PropTypes.shape({
    minAge: PropTypes.number,
    maxAge: PropTypes.number,
    gender: PropTypes.string,
    maxDistance: PropTypes.number
  }),
  autoFocus: PropTypes.bool,
  initialSearchTerm: PropTypes.string
};

export default AdvancedSearch;